import { useState } from "react";
import {
  Building2,
  Users,
  DollarSign,
  Calendar,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  TrendingUp,
  ChevronRight,
} from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../../../../components/ui/avatar";
import { cn } from "../../../../lib/utils";

const departmentIcons = {
  "Ressources Humaines": {
    icon: UserCheck,
    color: "text-blue-600",
    bg: "bg-blue-100",
  },
  Informatique: {
    icon: Building2,
    color: "text-purple-600",
    bg: "bg-purple-100",
  },
  Marketing: { icon: TrendingUp, color: "text-green-600", bg: "bg-green-100" },
  Commercial: {
    icon: DollarSign,
    color: "text-orange-600",
    bg: "bg-orange-100",
  },
  Production: { icon: Building2, color: "text-gray-600", bg: "bg-gray-100" },
  default: { icon: Building2, color: "text-blue-600", bg: "bg-blue-100" },
};

export default function DepartmentList({
  departments = [],
  onView,
  onEdit,
  onDelete,
  onViewEmployees,
}) {
  const [hoveredItem, setHoveredItem] = useState(null);

  const formatCurrency = (amount) => {
    if (!amount) return "Non défini";
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) return "Non défini";
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      notation: numericAmount > 1000000 ? "compact" : "standard",
    }).format(numericAmount);
  };

  const formatDate = (date) => {
    if (!date) return "Non défini";
    return new Intl.DateTimeFormat("fr-FR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(date));
  };

  const getDepartmentIcon = (name) => {
    const config = departmentIcons[name] || departmentIcons.default;
    return config;
  };

  if (departments.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">Aucun département trouvé</p>
        <p className="text-gray-400 text-sm">
          Ajustez vos filtres ou créez un nouveau département
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {departments.map((department) => {
        const iconConfig = getDepartmentIcon(department.nom);
        const IconComponent = iconConfig.icon;
        const budgetUtilization =
          department.budget && department._count?.employes
            ? Math.min(
                ((department._count.employes * 50000) /
                  parseFloat(department.budget)) *
                  100,
                100
              )
            : 0;

        return (
          <div
            key={department.id}
            className={cn(
              "group relative bg-white border border-gray-200 rounded-lg p-4 transition-all duration-200",
              "hover:border-gray-300 hover:shadow-md",
              hoveredItem === department.id && "border-blue-200 bg-blue-50/30"
            )}
            onMouseEnter={() => setHoveredItem(department.id)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div className="flex items-center justify-between">
              {/* Left Section - Main Info */}
              <div className="flex items-center space-x-4 flex-1 min-w-0">
                {/* Department Icon */}
                <div
                  className={cn(
                    "p-2.5 rounded-lg flex-shrink-0",
                    iconConfig.bg
                  )}
                >
                  <IconComponent className={cn("h-5 w-5", iconConfig.color)} />
                </div>

                {/* Department Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {department.nom}
                    </h3>
                    <Badge
                      variant={department.actif ? "default" : "secondary"}
                      className={cn(
                        "transition-all duration-200 flex-shrink-0",
                        department.actif
                          ? "bg-green-100 text-green-800 hover:bg-green-200"
                          : "bg-gray-100 text-gray-600"
                      )}
                    >
                      {department.actif ? "Actif" : "Inactif"}
                    </Badge>
                  </div>

                  {department.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {department.description}
                    </p>
                  )}

                  {/* Stats Row */}
                  <div className="flex items-center space-x-6 text-sm">
                    {/* Manager */}
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Manager:</span>
                      {department.manager ? (
                        <div className="flex items-center space-x-1">
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className="text-xs bg-blue-100 text-blue-600">
                              {department.manager.prenom?.[0]}
                              {department.manager.nom?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">
                            {department.manager.prenom} {department.manager.nom}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">
                          Non assigné
                        </span>
                      )}
                    </div>

                    {/* Employees */}
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Employés:</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewEmployees(department)}
                        className="h-auto p-1 hover:bg-blue-50"
                      >
                        <Badge
                          variant="secondary"
                          className="bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors cursor-pointer"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {department._count?.employes || 0}
                        </Badge>
                      </Button>
                    </div>

                    {/* Budget */}
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-500">Budget:</span>
                      <span className="font-medium">
                        {formatCurrency(department.budget)}
                      </span>
                    </div>

                    {/* Creation Date */}
                    <div className="flex items-center space-x-1 text-gray-500">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(department.date_creation)}</span>
                    </div>
                  </div>

                  {/* Budget Utilization Bar */}
                  {department.budget && budgetUtilization > 0 && (
                    <div className="mt-3 space-y-1">
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Utilisation estimée du budget</span>
                        <span>{budgetUtilization.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className={cn(
                            "h-1.5 rounded-full transition-all duration-500",
                            budgetUtilization > 80
                              ? "bg-red-500"
                              : budgetUtilization > 60
                              ? "bg-yellow-500"
                              : "bg-green-500"
                          )}
                          style={{ width: `${budgetUtilization}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Section - Actions */}
              <div className="flex items-center space-x-2 flex-shrink-0">
                {/* Quick Actions */}
                <div
                  className={cn(
                    "flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                    hoveredItem === department.id && "opacity-100"
                  )}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onView(department)}
                    className="h-8 w-8 p-0"
                    title="Voir détails"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewEmployees(department)}
                    className="h-8 w-8 p-0"
                    title="Voir employés"
                  >
                    <Users className="h-4 w-4" />
                  </Button>
                </div>

                {/* View Details Button */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onView(department)}
                  className="h-8 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <span className="text-sm">Détails</span>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>

                {/* More Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => onView(department)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Voir détails
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onViewEmployees(department)}
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Voir employés
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onEdit(department)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Modifier
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => onDelete(department, false)}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {department.actif ? "Désactiver" : "Supprimer"}
                    </DropdownMenuItem>
                    {!department.actif && (
                      <DropdownMenuItem
                        onClick={() => onDelete(department, true)}
                        className="text-red-800 focus:text-red-800"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Supprimer définitivement
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Hover Effect Border */}
            <div
              className={cn(
                "absolute inset-0 border-2 border-transparent rounded-lg pointer-events-none transition-colors duration-200",
                hoveredItem === department.id && "border-blue-200"
              )}
            />
          </div>
        );
      })}
    </div>
  );
}
